'use client';

import { Button } from '@common/components';
import { CustomError, ERROR_CODE, SENTRY_ERROR_BOUNDARY_SOURCE, SENTRY_EVENT_SOURCE_TAG } from '@core/utils';
import * as Sentry from '@sentry/nextjs';
import dayjs from 'dayjs';
import { randomInt } from 'es-toolkit';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PartnerEscapeNav } from '@/app/_components/PartnerEscapeNav';
import { PartnerHomeLink } from '@/app/_components/PartnerHomeLink';
import { Image } from '@/components/client';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAppBack } from '@/modules/wello-app/_hooks/useAppBack';
import { isDeploymentError } from '@/utils/isDeploymentError';
interface ErrorPageProps {
  error: Error & {
    digest?: string;
  };
  reset: () => void;
}
const DEPLOYMENT_RELOAD_STORAGE_KEY = 'deployment-error-reload';
const DEPLOYMENT_RELOAD_COOLDOWN_MS = 10_000;
const ErrorPage = ({
  error
}: ErrorPageProps) => {
  useAppBack();
  const router = useCustomRouter();
  const [errorId, setErrorId] = useState('');
  const errorCode = error instanceof CustomError ? error.return_code : undefined;
  const [shouldReload] = useState(() => isDeploymentError(error) && dayjs().diff(Number(sessionStorage.getItem(DEPLOYMENT_RELOAD_STORAGE_KEY) || 0)) > DEPLOYMENT_RELOAD_COOLDOWN_MS);
  useLayoutEffect(() => {
    if (shouldReload) {
      sessionStorage.setItem(DEPLOYMENT_RELOAD_STORAGE_KEY, String(dayjs().valueOf()));
      window.location.reload();
    }
  }, [shouldReload]);
  useEffect(() => {
    if (errorCode && [ERROR_CODE.HIDDEN].includes(errorCode)) return;
    const nextjsDigest = error.digest;
    const id = Sentry.captureException(error, {
      tags: {
        [SENTRY_EVENT_SOURCE_TAG]: SENTRY_ERROR_BOUNDARY_SOURCE,
        ...(nextjsDigest ? {
          nextjsDigest
        } : {})
      },
      ...(nextjsDigest ? {
        extra: {
          nextjsDigest
        }
      } : {})
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect -- Sentry SDK captureException 반환값(비동기 ID)을 state에 저장하는 유일한 지점
    setErrorId(id);
  }, [error, errorCode]);

  //! ⚠️ 2번 렌더링 되는것을 방지하기 위함
  const imgIndex = useRef(0);

  //* reload()는 페인트를 막지 못해 새 문서가 올 때까지 그린 화면이 남는다
  if (shouldReload) {
    return null;
  }
  return <article className="flex flex-1 flex-col items-center">
      <PartnerEscapeNav />
      {(() => {
      switch (errorCode) {
        case ERROR_CODE.HIDDEN:
          // eslint-disable-next-line react-hooks/refs -- 렌더 중 1회 초기화하는 안정적 randomInt 패턴, 2번 렌더링 방지 목적
          if (!imgIndex.current) {
            imgIndex.current = randomInt(1, 2);
          }
          return <>
                <section className="flex flex-1 flex-col items-center justify-center whitespace-pre-wrap p-20 text-center text-Gray-700 typo-body4">
                  <h1 className="mb-24 font-800 text-Gray-900 typo-h5">
                    해당 페이지를
                    <br />볼 수 없어요
                  </h1>
                  <p className="m-0 py-4">
                    이용 정책에 의해 비공개되었거나 삭제된 페이지예요.
                  </p>
                  <Image alt="Error" className="my-40 h-auto w-full animate-wiggle" height={186}
              // eslint-disable-next-line react-hooks/refs -- 렌더 중 1회 초기화하는 안정적 randomInt 패턴, 2번 렌더링 방지 목적
              src={`/assets/pngs/error/hidden/${imgIndex.current}.png`} width={335} />
                </section>
                <nav className="mt-auto flex w-full gap-8 px-20 py-40 [&>button]:flex-1">
                  <Button theme="sub-line" onClick={() => router.back()}>
                    돌아가기
                  </Button>
                  <Button asChild className="flex-1">
                    <PartnerHomeLink>홈으로 이동</PartnerHomeLink>
                  </Button>
                </nav>
              </>;
        default:
          // eslint-disable-next-line react-hooks/refs -- 렌더 중 1회 초기화하는 안정적 randomInt 패턴, 2번 렌더링 방지 목적
          if (!imgIndex.current) {
            imgIndex.current = randomInt(1, 2);
          }
          return <>
                <section className="flex flex-1 flex-col items-center justify-center whitespace-pre-wrap p-20 text-center text-Gray-700 typo-body4 [&>.inquiry-button]:font-400 [&>.inquiry-button]:text-Gray-700">
                  <h1 className="mb-24 font-800 text-Gray-900 typo-h5">
                    생각지 못한 오류가
                    <br />
                    발생했어요
                  </h1>
                  <p className="m-0 py-4">
                    새로 고침을 선택하거나 홈으로 이동해 주세요.
                  </p>
                  <p className="m-0 py-4">
                    반복되는 오류는 고객 문의로 문의해 주시면
                    <br />
                    빠르게 처리해 드리겠습니다.
                  </p>
                  <p className="m-0 py-4 text-Gray-700">{errorId}</p>
                  <Image alt="Error" className="my-40 h-auto w-full animate-wiggle" height={186}
              // eslint-disable-next-line react-hooks/refs -- 렌더 중 1회 초기화하는 안정적 randomInt 패턴, 2번 렌더링 방지 목적
              src={`/assets/pngs/error/${imgIndex.current}.png`} width={335} />
                </section>
                <nav className="mt-auto flex w-full gap-8 px-20 py-40 [&>button]:flex-1">
                  <Button theme="sub-line" onClick={() => window.location.reload()} className="flex-1">
                    새로고침
                  </Button>
                  <Button asChild className="flex-1">
                    <PartnerHomeLink>홈으로 이동</PartnerHomeLink>
                  </Button>
                </nav>
              </>;
      }
    })()}
    </article>;
};
export default ErrorPage;